import React from "react";
import {Field, useFormState} from "react-final-form";
import useCalculator from "../../hooks/useCalculator";
import usePrice from "../../hooks/usePrice";
import {FREEBIES_TOTAL_VALUE} from "../../constants/Freebies";

function OrderTotal({existingOrder = null}) {
    const {price, priceOriginal, balanceApplied: rawBalanceApplied, priceAfterBalance, customerBalance} = useCalculator();
    const {priceFormat} = usePrice();
    const {values: formValues} = useFormState();

    // Detect a partially-paid order — only set on the edit page when the
    // backend reports a paid_price > 0. The calculator re-sums the full
    // price from selected services, but the customer really owes the
    // existing `unpaid_price` remainder. Mirrors the resume edit fix.
    const merchantPaidPrice = parseFloat(existingOrder?.merchant_paid_price || 0) || 0;
    const unpaidPrice = parseFloat(existingOrder?.unpaid_price || 0) || 0;
    const isPartiallyPaid = merchantPaidPrice > 0;

    // SC already committed to this order — always deducted by the backend
    // on edit regardless of the useBalance checkbox. Not refundable.
    const committedSC = parseFloat(existingOrder?.sc_amount || 0) || 0;

    const balanceAvailable = customerBalance > 0;
    const useBalance = !!formValues.useBalance;

    let balanceApplied;
    let finalPrice;
    let strikeAnchor;
    let showStrike;

    if (isPartiallyPaid) {
        // Coupon was already settled on the original order; the remainder
        // is what's left. Cap balance at it, no list-price strike to show.
        balanceApplied = useBalance && customerBalance > 0
            ? Math.min(customerBalance, unpaidPrice)
            : 0;
        finalPrice = Math.max(0, unpaidPrice - balanceApplied);
        strikeAnchor = unpaidPrice;
        showStrike = false;
    } else if (committedSC > 0) {
        // SC is committed on this order. useBalance controls whether to keep
        // it (checked) or cancel and restore to customer's account (unchecked).
        // Price: keep SC → subtract it; cancel SC → show full calculator price.
        const priceAfterSC = useBalance ? Math.max(0.01, price - committedSC) : price;
        balanceApplied = 0;
        finalPrice = priceAfterSC;
        const savingFromDiscountSC = Math.max(0, priceOriginal - finalPrice);
        strikeAnchor = finalPrice + FREEBIES_TOTAL_VALUE + savingFromDiscountSC;
        showStrike = strikeAnchor > finalPrice + 0.005;
    } else {
        balanceApplied = rawBalanceApplied;
        finalPrice = balanceApplied > 0 ? priceAfterBalance : price;
        const basePrice = balanceApplied > 0 ? price : finalPrice;
        const savingFromDiscount = Math.max(0, priceOriginal - basePrice);
        strikeAnchor = finalPrice + FREEBIES_TOTAL_VALUE + savingFromDiscount;
        showStrike = strikeAnchor > finalPrice + 0.005;
    }

    const fullyCovered = balanceApplied > 0 && finalPrice <= 0;
    const remainingAfterApply = Math.max(0, customerBalance - balanceApplied);
    const totalSaving = Math.max(0, strikeAnchor - finalPrice);

    return <>
        {(committedSC > 0 || balanceAvailable) && (
            <Field name="useBalance" type="checkbox">
                {({input}) => (
                    <label className={`balance-card ${input.checked ? 'is-active' : ''}`}>
                        <span className="balance-card__icon" aria-hidden="true">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2.5" y="6" width="19" height="13" rx="2.5"/>
                                <path d="M2.5 10h19"/>
                                <circle cx="17" cy="14.5" r="1.2" fill="currentColor"/>
                            </svg>
                        </span>
                        <span className="balance-card__body">
                            {committedSC > 0 ? (
                                <>
                                    <span className="balance-card__title">Account credit</span>
                                    <span className="balance-card__sub">
                                        {input.checked
                                            ? <>{priceFormat(committedSC)} applied to this order</>
                                            : <>Uncheck to restore {priceFormat(committedSC)} to your balance</>
                                        }
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span className="balance-card__title">Use my account credit</span>
                                    {input.checked ? (
                                        <span className="balance-card__sub">
                                            Applying <strong>{priceFormat(balanceApplied)}</strong>
                                            {remainingAfterApply > 0 && (
                                                <> · {priceFormat(remainingAfterApply)} left</>
                                            )}
                                        </span>
                                    ) : (
                                        <span className="balance-card__sub">
                                            Available: <strong>{priceFormat(customerBalance)}</strong>
                                        </span>
                                    )}
                                </>
                            )}
                        </span>
                        <input type="checkbox" className="balance-card__input" {...input} />
                        <span className="balance-card__check" aria-hidden="true">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"/>
                            </svg>
                        </span>
                    </label>
                )}
            </Field>
        )}

        <div className="order-total">
            <div className="order-total__text">Order Total</div>
            <div className="order-total__price">
                <div className="order-total__price-box">
                    {showStrike && (
                        <span className="order-total__price-features">{priceFormat(strikeAnchor)}</span>
                    )}
                    <span className={fullyCovered ? 'order-total__price-zero' : ''}>{priceFormat(finalPrice)}</span>
                </div>
                {committedSC > 0 && useBalance && (
                    <span className="order-total__credit-line">
                        Account credit −{priceFormat(committedSC)}
                    </span>
                )}
                {balanceApplied > 0 && (
                    <span className="order-total__credit-line">
                        Account credit −{priceFormat(balanceApplied)}
                    </span>
                )}
                {totalSaving > 0 && (
                    <span className="order-total__price-saving">You are saving {priceFormat(totalSaving)}</span>
                )}
            </div>
        </div>
    </>
}

export default OrderTotal;
